import { useState, useEffect, useCallback, useMemo } from 'react';

export type ModalMode = 'EDIT' | 'VIEW';

// Optimized deep comparison function that's faster than JSON.stringify
const deepEqual = <T>(obj1: T, obj2: T): boolean => {
  if (obj1 === obj2) return true;

  if (obj1 == null || obj2 == null) return obj1 === obj2;
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object')
    return obj1 === obj2;

  const keys1 = Object.keys(obj1 as object);
  const keys2 = Object.keys(obj2 as object);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!deepEqual((obj1 as any)[key], (obj2 as any)[key])) return false;
  }

  return true;
};

export interface UseEditableModalProps<T> {
  entity: T;
  initialMode?: ModalMode;
  onSave: (editedEntity: T) => void | Promise<void>;
  onClose: () => void;
  alwaysDisabledFields?: string[];
  styles: Record<string, React.CSSProperties>; // Modal styles object
}

export interface UseEditableModalReturn<T> {
  // State
  originalEntity: T;
  editedEntity: T;
  setEditedEntity: (entity: T) => void;
  modalMode: ModalMode;
  setModalMode: (mode: ModalMode) => void;
  focusedField: string | null;
  setFocusedField: (field: string | null) => void;

  // Computed values
  hasUnsavedChanges: boolean;

  // Actions
  handleSave: () => void;
  handleCancel: () => void;
  handleClose: () => void;
  handleEdit: () => void;

  // Field helpers
  isFieldDisabled: (fieldName: string) => boolean;
  getFloatingLabelStyle: (fieldName: string, hasValue: boolean) => object;
  getFloatingInputStyle: (isTextarea: boolean, fieldName: string) => object;
  getFloatingSelectStyle: (fieldName: string) => object;

  // Button styles
  getSaveButtonStyle: () => object;
  getSaveButtonHoverStyle: () => object;
  getSaveButtonFocusStyle: () => object;
  getCloseButtonStyle: () => object;
  getCloseButtonHoverStyle: () => object;
  getCloseButtonFocusStyle: () => object;
  getSaveButtonText: () => string;
  getCloseButtonText: () => string;
}

const useEditableModal = <T>({
  entity,
  initialMode = 'VIEW',
  onSave,
  onClose,
  alwaysDisabledFields = [],
  styles,
}: UseEditableModalProps<T>): UseEditableModalReturn<T> => {
  // State management
  const [originalEntity] = useState<T>(entity);
  const [editedEntity, setEditedEntity] = useState<T>(entity);
  const [modalMode, setModalMode] = useState<ModalMode>(initialMode);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  // Check if entity has been modified - memoized for performance
  const hasUnsavedChanges = useMemo((): boolean => {
    return !deepEqual(editedEntity, originalEntity);
  }, [editedEntity, originalEntity]);

  // Field state helpers
  const isFieldDisabled = useCallback(
    (fieldName: string): boolean => {
      if (modalMode === 'VIEW') return true;
      return alwaysDisabledFields.includes(fieldName);
    },
    [modalMode, alwaysDisabledFields]
  );

  // Style helpers
  const getFloatingLabelStyle = useCallback(
    (fieldName: string, hasValue: boolean) => {
      if (!styles) return {};

      const isDisabled = isFieldDisabled(fieldName);
      const isFocused = focusedField === fieldName;

      return {
        ...styles.floatingLabelStyle,
        ...(isFocused || hasValue ? styles.floatingLabelFocusedStyle : {}),
        ...(isDisabled ? styles.floatingLabelDisabledStyle : {}),
      };
    },
    [styles, focusedField, isFieldDisabled]
  );

  const getFloatingInputStyle = useCallback(
    (isTextarea: boolean, fieldName: string) => {
      if (!styles) return {};

      const isDisabled = isFieldDisabled(fieldName);
      const isFocused = focusedField === fieldName;

      return {
        ...(isTextarea
          ? styles.floatingTextareaStyle
          : styles.floatingInputStyle),
        ...(isFocused && !isDisabled ? styles.floatingInputFocusedStyle : {}),
        ...(isDisabled ? styles.floatingInputDisabledStyle : {}),
      };
    },
    [styles, focusedField, isFieldDisabled]
  );

  const getFloatingSelectStyle = useCallback(
    (fieldName: string) => {
      if (!styles) return {};

      const isDisabled = isFieldDisabled(fieldName);
      const isFocused = focusedField === fieldName;

      return {
        ...styles.floatingSelectStyle,
        ...(isFocused && !isDisabled ? styles.floatingInputFocusedStyle : {}),
        ...(isDisabled ? styles.floatingSelectDisabledStyle : {}),
      };
    },
    [styles, focusedField, isFieldDisabled]
  );

  // Button styling with hover/focus support
  const getSaveButtonStyle = useCallback(() => {
    if (!styles) return {};

    if (modalMode === 'VIEW') {
      return {
        ...styles.modalSaveButtonStyle,
        ...styles.modalEditButtonStyle,
      };
    }

    if (!hasUnsavedChanges) {
      return {
        ...styles.modalSaveButtonStyle,
        ...styles.modalDisabledButtonStyle,
      };
    }

    return styles.modalSaveButtonStyle;
  }, [styles, modalMode, hasUnsavedChanges]);

  const getSaveButtonHoverStyle = useCallback(() => {
    if (!styles) return {};

    // Don't hover disabled buttons
    if (modalMode === 'EDIT' && !hasUnsavedChanges) {
      return getSaveButtonStyle(); // Return disabled style, no hover
    }

    if (modalMode === 'VIEW') {
      return {
        ...styles.modalSaveButtonStyle,
        ...styles.modalEditButtonStyle,
        ...styles.modalEditButtonHoverStyle,
      };
    }

    return {
      ...styles.modalSaveButtonStyle,
      ...styles.modalSaveButtonHoverStyle,
    };
  }, [styles, modalMode, hasUnsavedChanges, getSaveButtonStyle]);

  const getSaveButtonFocusStyle = useCallback(() => {
    if (!styles) return {};
    return {
      ...getSaveButtonStyle(),
      ...styles.modalSaveButtonFocusStyle,
    };
  }, [styles, getSaveButtonStyle]);

  const getCloseButtonStyle = useCallback(() => {
    if (!styles) return {};
    return styles.modalCloseButtonStyle;
  }, [styles]);

  const getCloseButtonHoverStyle = useCallback(() => {
    if (!styles) return {};
    return {
      ...styles.modalCloseButtonStyle,
      ...styles.modalCloseButtonHoverStyle,
    };
  }, [styles]);

  const getCloseButtonFocusStyle = useCallback(() => {
    if (!styles) return {};
    return {
      ...styles.modalCloseButtonStyle,
      ...styles.modalCloseButtonFocusStyle,
    };
  }, [styles]);

  // Button text
  const getSaveButtonText = useCallback(() => {
    return modalMode === 'VIEW' ? 'Edit' : 'Save';
  }, [modalMode]);

  const getCloseButtonText = useCallback(() => {
    return modalMode === 'VIEW' ? 'Close' : 'Cancel';
  }, [modalMode]);

  // Action handlers
  const handleEdit = useCallback(() => {
    setModalMode('EDIT');
  }, []);

  const handleSave = useCallback(async () => {
    if (modalMode === 'VIEW') {
      handleEdit();
      return;
    }

    if (!hasUnsavedChanges) {
      return; // No changes to save
    }

    try {
      await onSave(editedEntity);
    } catch (error) {
      console.error('❌ Modal save operation failed:', error);
      // Let the parent component handle user feedback
      // The error is re-thrown so parent handlers can catch it
      throw error;
    }
  }, [modalMode, hasUnsavedChanges, editedEntity, onSave, handleEdit]);

  const handleCancel = useCallback(() => {
    if (modalMode === 'EDIT' && hasUnsavedChanges) {
      const confirmDiscard = window.confirm(
        'You have unsaved changes. Are you sure you want to discard them?'
      );
      if (!confirmDiscard) {
        return;
      }
    }

    if (modalMode === 'EDIT' && initialMode === 'VIEW') {
      // Reset to original entity and switch to view mode
      setEditedEntity(originalEntity);
      setModalMode('VIEW');
    } else {
      // In view mode, just close
      onClose();
    }
  }, [modalMode, hasUnsavedChanges, originalEntity, onClose, initialMode]);

  const handleClose = useCallback(() => {
    handleCancel(); // Use the same logic as cancel
  }, [handleCancel]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // ESC key to cancel/close
      if (event.key === 'Escape') {
        event.preventDefault();
        handleCancel();
      }

      // Ctrl+Enter (or Cmd+Enter on Mac) to save
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault();
        handleSave();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleCancel, handleSave]);

  return {
    // State
    originalEntity,
    editedEntity,
    setEditedEntity,
    modalMode,
    setModalMode,
    focusedField,
    setFocusedField,

    // Computed values
    hasUnsavedChanges,

    // Actions
    handleSave,
    handleCancel,
    handleClose,
    handleEdit,

    // Field helpers
    isFieldDisabled,
    getFloatingLabelStyle,
    getFloatingInputStyle,
    getFloatingSelectStyle,

    // Button styles
    getSaveButtonStyle,
    getSaveButtonHoverStyle,
    getSaveButtonFocusStyle,
    getCloseButtonStyle,
    getCloseButtonHoverStyle,
    getCloseButtonFocusStyle,
    getSaveButtonText,
    getCloseButtonText,
  };
};

export default useEditableModal;
